import { useMemo, type ReactNode } from "react";
import type { PlanStarter } from "@heraion/shared";
import { TAB_ICON, type IconName } from "@heraion/ui";

import { CoachDashboardScreen } from "../CoachDashboardScreen";
import { CoachLibraryScreen } from "../CoachLibraryScreen";
import { InboxScreen } from "../InboxScreen";
import { PlanScreen } from "../PlanScreen";
import { ProgressScreen } from "../ProgressScreen";
import { WorkoutsScreen } from "../WorkoutsScreen";
import type { Experience } from "../experience";
import type { SettingsSubKey } from "../SettingsScreen";
import type { ComposerRequest } from "../useCreateHubWeb";

// `hidden`: a real, reachable surface (route() lands on it) that gets no bar or
// rail entry. The coach Library lives in the + hub since 13/10 (#352).
export type Surface = {
  key: string;
  label: string;
  icon: IconName;
  render: () => ReactNode;
  hidden?: boolean;
};

// AppShell's per-experience tab list, split out to keep AppShell.tsx under
// the file-size cap (prompt.md §Limiti di dimensione). Pure move: same three
// lists (coach / admin / athlete), same deps, same memo, no behaviour change.
// Settings is shared across all three; the athlete set is the Plan hub +
// Progress; admin is the feedback inbox.
export function useAppShellSurfaces(params: {
  experience: Experience;
  t: (key: string, options?: Record<string, unknown>) => string;
  dark: boolean;
  composerRequest: ComposerRequest | null;
  setComposerRequest: (req: ComposerRequest | null) => void;
  athleteSubs: ReadonlyArray<string>;
  coachReady: boolean;
  callerSub: string;
  renderSettings: () => ReactNode;
  requestOpenSettings: (sub: SettingsSubKey) => void;
  requestStartWorkout: (starter?: PlanStarter) => void;
  selectTab: (key: string) => void;
  openCreateHub: () => void;
}): Surface[] {
  const {
    experience,
    t,
    dark,
    composerRequest,
    setComposerRequest,
    athleteSubs,
    coachReady,
    callerSub,
    renderSettings,
    requestOpenSettings,
    requestStartWorkout,
    selectTab,
    openCreateHub,
  } = params;

  return useMemo(
    () =>
      experience === "coach"
        ? [
            {
              key: "coachDashboard",
              icon: TAB_ICON.athletes,
              label: t("coach.dashboard.title"),
              render: () => (
                <CoachDashboardScreen
                  athleteSubs={coachReady ? athleteSubs : null}
                />
              ),
            },
            // CO-19: the coach's own Library surface (mobile's twin:
            // CoachLibraryStackNavigator's CoachLibraryScreen). Its own key,
            // added 20/09: library.header is "Browse"/"Sfoglia", an imperative
            // written for a screen heading, and it is ALSO the label above the
            // filter chips inside this very screen (LibraryBrowser.tsx:314),
            // so borrowing it printed the same word twice, once as a place and
            // once as an action. A tab is named for a place.
            {
              key: "library",
              hidden: true,
              icon: TAB_ICON.library,
              label: t("mobile.tabs.library", { defaultValue: "Library" }),
              render: () => (
                <CoachLibraryScreen
                  composerRequest={composerRequest}
                  onComposerRequestHandled={() => setComposerRequest(null)}
                  callerSub={callerSub}
                  athleteRoster={coachReady ? athleteSubs : undefined}
                />
              ),
            },
            {
              key: "settings",
              icon: TAB_ICON.settings,
              label: t("mobile.tabs.settings", { defaultValue: "Settings" }),
              render: renderSettings,
            },
          ]
        : experience === "admin"
        ? [
            {
              key: "inbox",
              icon: TAB_ICON.inbox,
              label: t("feedback.inboxTitle"),
              render: () => <InboxScreen dark={dark} />,
            },
            {
              key: "settings",
              icon: TAB_ICON.settings,
              label: t("mobile.tabs.settings", { defaultValue: "Settings" }),
              render: renderSettings,
            },
          ]
        : [
            {
              key: "plan",
              icon: TAB_ICON.plan,
              label: t("mobile.tabs.plan"),
              render: () => (
                <PlanScreen
                  onOpenProgress={() => selectTab("progress")}
                  // HR-11: the Home pain-map card's tap, same page as the
                  // logger's veto line below.
                  onOpenPainMap={() => requestOpenSettings("painMap")}
                  composerRequest={composerRequest}
                  onComposerRequestHandled={() => setComposerRequest(null)}
                  onStartWorkout={requestStartWorkout}
                  onOpenCreateHub={openCreateHub}
                  // PE-48: the SAME single useCoachStatus read above feeds
                  // the assignee panel's roster; no second /api/me/coach-edges
                  // fetch (CN-coach-edges-fetch).
                  callerSub={callerSub}
                  // PE-48 review fix #3: `[]` is truthy, so
                  // `callerSub && athleteRoster` in ProgramComposer/
                  // PlanComposer mounted the assignee band immediately with
                  // an empty roster, showing "No athletes in your roster
                  // yet." to a coach whose roster just hadn't resolved yet
                  // (and feeding useProfileNames a roster that flips from
                  // empty to populated mid-flight, review fix #2's
                  // trigger). `undefined` makes the guard hold off exactly
                  // like mobile already does by construction (its
                  // `callerSub` stays null until useCoachEdges resolves).
                  athleteRoster={coachReady ? athleteSubs : undefined}
                />
              ),
            },
            {
              key: "workouts",
              icon: TAB_ICON.workouts,
              label: t("mobile.tabs.workouts"),
              render: () => (
                <WorkoutsScreen
                  composerRequest={composerRequest}
                  onComposerRequestHandled={() => setComposerRequest(null)}
                  onOpenPainMap={() => requestOpenSettings("painMap")}
                  onOpenCreateHub={openCreateHub}
                />
              ),
            },
            {
              key: "progress",
              icon: TAB_ICON.progress,
              label: t("mobile.tabs.progress"),
              render: () => (
                <ProgressScreen onStartWorkout={requestStartWorkout} />
              ),
            },
            {
              key: "settings",
              icon: TAB_ICON.settings,
              label: t("mobile.tabs.settings", { defaultValue: "Settings" }),
              render: renderSettings,
            },
          ],
    // composerRequest is read inside the plan surface's render closure: it
    // MUST invalidate the memo or PlanScreen mounts with a stale null.
    // athleteSubs + coachReady MUST be here: the coach surface closes over
    // them, and they arrive async. Leaving them out pinned the closure to
    // the empty roster it saw on the first render, so opening straight into
    // coach showed a dashboard of zeroes forever and only a re-memo
    // (switching experience) fixed it. `callerSub` used to stand here and
    // was DROPPED 2026-08-14, when web-rnw joined the lint gate and said
    // nothing in the memo read it. ⚠️ PE-48 put it back, on purpose this
    // time: the plan surface's render closure now passes
    // `callerSub={callerSub}` to PlanScreen (the assignee panel's self row),
    // so it IS read here again, the 2026-08-14 premise ("nothing in the memo
    // reads it") is false again, and the lint gate that caught it the first
    // time caught it again. renderSettings/requestOpenSettings/
    // requestStartWorkout are their own useCallbacks (stable unless
    // settingsSub changes, or never for requestStartWorkout), listed so a
    // real change still invalidates the memo, the same stale-closure trap
    // the comment above already names (2026-08-14) for athleteSubs/coachReady.
    // setComposerRequest is a useState setter, selectTab a memoized callback (stable identity)
    // AppShell passes straight through; listed only because this memo now
    // lives in its own file, where eslint can no longer see the `useState`
    // call two hops up to infer that on its own (react-hooks/exhaustive-deps).
    [
      experience,
      t,
      dark,
      composerRequest,
      setComposerRequest,
      athleteSubs,
      coachReady,
      callerSub,
      renderSettings,
      requestOpenSettings,
      requestStartWorkout,
      selectTab,
      openCreateHub,
    ],
  );
}
